import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Table,
  Spinner,
  Input,
  Badge,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import axios from "axios"
import { baseUrl } from "../../Network"
import { handleAuthError, newFormatDate } from "../../utils"
import { toast } from "react-toastify"
import PaginationControls from "../../components/Common/PaginationControls"
import ActionsDropdown from "../../components/Common/ActionsDropdown"
import FilterBarCollapsible from "../../components/Common/FilterBarCollapsible"
import { useConfirm } from "../../contexts/ConfirmContext"
import { isMarketerReadOnly } from "../../helpers/adminRole"
import ActivateAiTransferModal from "../../components/Common/ActivateAiTransferModal"

const ACCESS_FILTERS = [
  { value: "", label: "All access types" },
  { value: "ai_app_only", label: "AI app only" },
  { value: "normal_and_ai", label: "Normal + AI" },
  { value: "normal_platform", label: "Normal platform" },
]

const accessBadgeColor = (key) => {
  if (key === "ai_app_only") return "primary"
  if (key === "normal_and_ai") return "info"
  if (key === "normal_platform") return "secondary"
  return "light"
}

const ManageAiSubscribers = () => {
  document.title = "AI subscribers | RYD Admin"
  const { showConfirm } = useConfirm()
  const readOnly = isMarketerReadOnly()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [accessFilter, setAccessFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPreviousPage: false,
  })
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [activateTransferModal, setActivateTransferModal] = useState(false)
  const [activateTransferParent, setActivateTransferParent] = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${baseUrl}/admin/subscription/subscribers`, {
        params: {
          page: currentPage,
          limit: pageSize,
          search: search.trim() || undefined,
          access: accessFilter || undefined,
        },
      })
      if (res?.data?.status) {
        const list = Array.isArray(res.data.data?.items) ? res.data.data.items : []
        const pag = res.data.data?.pagination || pagination
        setItems(list)
        setPagination(pag)
      }
    } catch (e) {
      handleAuthError(e)
    } finally {
      setLoading(false)
    }
  }, [currentPage, pageSize, search, accessFilter])

  useEffect(() => {
    load()
  }, [load])

  const handleSearch = (e) => {
    e.preventDefault()
    if (currentPage === 1) load()
    else setCurrentPage(1)
  }

  const cancelSubscription = async (row, immediate) => {
    setActionLoadingId(row.parentId)
    try {
      const res = await axios.post(`${baseUrl}/admin/subscription/cancel/${row.parentId}`, {
        immediate,
      })
      if (res?.data?.status) {
        toast.success(res.data.message || "Subscription updated")
        await load()
      } else {
        toast.error(res?.data?.message || "Failed")
      }
    } catch (e) {
      handleAuthError(e)
      toast.error(e?.response?.data?.message || "Failed to cancel subscription")
    } finally {
      setActionLoadingId(null)
    }
  }

  const openActivateTransferModal = (row) => {
    setActivateTransferParent(row || null)
    setActivateTransferModal(true)
  }

  const activateAiByTransfer = async (duration) => {
    if (!activateTransferParent?.parentId) return
    setActionLoadingId(activateTransferParent.parentId)
    try {
      const payload =
        duration?.days != null
          ? { days: Number(duration.days) }
          : { months: Number(duration?.months ?? duration) }
      const res = await axios.post(
        `${baseUrl}/admin/subscription/activate-transfer/${activateTransferParent.parentId}`,
        payload
      )
      if (res?.data?.status) {
        toast.success(res.data.message || "AI access activated")
        setActivateTransferModal(false)
        setActivateTransferParent(null)
        await load()
      } else {
        toast.error(res?.data?.message || "Failed")
      }
    } catch (e) {
      handleAuthError(e)
      toast.error(e?.response?.data?.message || "Failed to activate subscription")
    } finally {
      setActionLoadingId(null)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Manage AI" breadcrumbItem="Subscribed parents" />
        <Row className="mb-3">
          <Col>
            <p className="text-muted mb-0">
              All parents with an active AI subscription — including normal-platform parents who subscribed to AI.
            </p>
          </Col>
        </Row>

        <FilterBarCollapsible title="Search & filter" defaultOpen>
          <form onSubmit={handleSearch} className="row g-2 align-items-end mb-3">
            <Col md={5}>
              <label className="form-label small text-muted">Search</label>
              <Input
                placeholder="Name, email, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <label className="form-label small text-muted">Access type</label>
              <Input
                type="select"
                value={accessFilter}
                onChange={(e) => {
                  setAccessFilter(e.target.value)
                  setCurrentPage(1)
                }}
              >
                {ACCESS_FILTERS.map((f) => (
                  <option key={f.value || "all"} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <button type="submit" className="btn btn-primary w-100">
                Search
              </button>
            </Col>
          </form>
        </FilterBarCollapsible>

        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    Subscribed parents
                    {!loading && (
                      <span className="text-muted fw-normal fs-6 ms-2">({pagination.totalItems})</span>
                    )}
                  </h5>
                  <Link to="/ai/parents" className="btn btn-sm btn-outline-secondary">
                    AI parents list
                  </Link>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : items.length === 0 ? (
                  <p className="text-muted text-center py-4 mb-0">No active AI subscribers.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Parent</th>
                          <th>Lead Source</th>
                          <th>Access</th>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Subscribed date</th>
                          <th>Period end</th>
                          <th>Cancel at end</th>
                          {!readOnly && <th className="text-end">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((row) => {
                          const sub = row.subscription || {}
                          const name = [row.firstName, row.lastName].filter(Boolean).join(" ") || "—"
                          return (
                            <tr key={row.parentId}>
                              <td>
                                <div className="fw-medium">{name}</div>
                                <small className="text-muted d-block">{row.email}</small>
                                {row.country && (
                                  <small className="text-muted d-block">{row.country}</small>
                                )}
                              </td>
                              <td>{row.survey || "—"}</td>
                              <td>
                                <Badge color={accessBadgeColor(row.accessType)} className="font-size-11">
                                  {row.accessTypeLabel}
                                </Badge>
                              </td>
                              <td>{sub.planKey || sub.plan?.name || "—"}</td>
                              <td>
                                <Badge
                                  color={
                                    sub.status === "active" || sub.status === "trialing"
                                      ? "success"
                                      : "warning"
                                  }
                                >
                                  {sub.status || "—"}
                                </Badge>
                              </td>
                              <td>
                                {row.subscribedAt || sub.currentPeriodStart
                                  ? newFormatDate(row.subscribedAt || sub.currentPeriodStart)
                                  : "—"}
                              </td>
                              <td>
                                {sub.currentPeriodEnd
                                  ? newFormatDate(sub.currentPeriodEnd)
                                  : "—"}
                              </td>
                              <td>{sub.cancelAtPeriodEnd ? "Yes" : "No"}</td>
                              {!readOnly && (
                              <td className="text-end">
                                <ActionsDropdown
                                  loading={actionLoadingId === row.parentId}
                                  items={[
                                    {
                                      label: "View in AI parents",
                                      icon: "mdi-account",
                                      onClick: () =>
                                        window.open(
                                          `/ai/parents?search=${encodeURIComponent(row.email || "")}`,
                                          "_self"
                                        ),
                                    },
                                    {
                                      label: "Activate AI by transfer",
                                      icon: "mdi-bank-transfer",
                                      variant: "success",
                                      onClick: () => openActivateTransferModal(row),
                                    },
                                    {
                                      label: "Cancel at period end",
                                      icon: "mdi-calendar-clock",
                                      variant: "warning",
                                      onClick: () =>
                                        showConfirm({
                                          title: "Cancel at period end",
                                          message: `Schedule cancellation for ${name}? They keep access until the billing period ends.`,
                                          variant: "primary",
                                        }).then((ok) => {
                                          if (ok) cancelSubscription(row, false)
                                        }),
                                    },
                                    {
                                      label: "Cancel immediately",
                                      icon: "mdi-close-octagon-outline",
                                      variant: "danger",
                                      onClick: () =>
                                        showConfirm({
                                          title: "Cancel immediately",
                                          message: `Cancel ${name}'s subscription now? They lose paid access immediately.`,
                                          variant: "danger",
                                        }).then((ok) => {
                                          if (ok) cancelSubscription(row, true)
                                        }),
                                    },
                                  ]}
                                />
                              </td>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {!loading && pagination.totalItems > 0 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pageSize}
                    showAll={pageSize >= 10000}
                    paginatedDataLength={items.length}
                    handlePageChange={setCurrentPage}
                    handlePageSizeChange={(size) => {
                      if (size === "all") {
                        setPageSize(10000)
                      } else {
                        setPageSize(Number(size) || 20)
                      }
                      setCurrentPage(1)
                    }}
                    goToNextPage={() => {
                      if (pagination.hasNextPage) setCurrentPage((p) => p + 1)
                    }}
                    goToPreviousPage={() => {
                      if (pagination.hasPreviousPage) setCurrentPage((p) => Math.max(1, p - 1))
                    }}
                    canPreviousPage={pagination.hasPreviousPage}
                    canNextPage={pagination.hasNextPage}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ActivateAiTransferModal
        isOpen={activateTransferModal}
        toggle={() => {
          if (actionLoadingId) return
          setActivateTransferModal(!activateTransferModal)
          if (activateTransferModal) setActivateTransferParent(null)
        }}
        parentLabel={
          activateTransferParent
            ? `${activateTransferParent.firstName || ""} ${activateTransferParent.lastName || ""}`.trim()
            : ""
        }
        loading={!!activateTransferParent && actionLoadingId === activateTransferParent.parentId}
        onSubmit={activateAiByTransfer}
      />
    </div>
  )
}

export default withAuth(ManageAiSubscribers)
